import { FACTOR_REFERENCES } from "../KPIGrid/model/helpers";

/** The values of a factor that each cite their own reference, in the order they are shown. */
export const REFERENCE_TARGETS = Object.freeze(FACTOR_REFERENCES.map(({ target, label, short }) => ({ target, label, short })));

/**
 * @param {import("./referencesApi").GhgReference|null|undefined} reference
 * @returns {string} the title, with its note when it has one, for a tooltip
 */
export const referenceTooltip = (reference) =>
  reference ? [reference.title, reference.note].filter(Boolean).join(" — ") : "";

/**
 * @typedef {object} FactorSelection
 * @property {boolean} allMatching every factor the filters match, loaded or not
 * @property {Set<number>} ids factors picked one by one; ignored while allMatching
 */

/** @type {FactorSelection} */
export const EMPTY_SELECTION = Object.freeze({ allMatching: false, ids: new Set() });

/**
 * @param {FactorSelection} selection
 * @param {number} totalFactors every factor the filters match
 * @returns {number} how many factors a change would reach
 */
export const selectedCount = (selection, totalFactors) => (selection.allMatching ? totalFactors : selection.ids.size);

/**
 * @param {FactorSelection} selection
 * @param {number} factorId
 * @returns {boolean}
 */
export const isFactorSelected = (selection, factorId) => selection.allMatching || selection.ids.has(factorId);

/**
 * Picks or drops one factor. Dropping one while every match is selected
 * would need the ids of pages never loaded, so it starts over from the
 * loaded factors minus that one.
 *
 * @param {FactorSelection} selection
 * @param {number} factorId
 * @param {number[]} loadedIds every factor on the loaded pages
 * @returns {FactorSelection}
 */
export const toggleFactor = (selection, factorId, loadedIds) => {
  if (selection.allMatching) {
    return { allMatching: false, ids: new Set(loadedIds.filter((id) => id !== factorId)) };
  }
  const ids = new Set(selection.ids);
  if (ids.has(factorId)) ids.delete(factorId);
  else ids.add(factorId);
  return { allMatching: false, ids };
};

/**
 * Picks every factor of a KPI, or drops them all when every one is picked.
 *
 * @param {FactorSelection} selection
 * @param {number[]} kpiFactorIds
 * @param {number[]} loadedIds every factor on the loaded pages
 * @returns {FactorSelection}
 */
export const toggleFactors = (selection, kpiFactorIds, loadedIds) => {
  const current = selection.allMatching ? new Set(loadedIds) : selection.ids;
  const allPicked = kpiFactorIds.every((id) => current.has(id));
  const ids = new Set(current);
  kpiFactorIds.forEach((id) => (allPicked ? ids.delete(id) : ids.add(id)));
  return { allMatching: false, ids };
};

/**
 * The request body that applies, or with `referenceId` null clears, a
 * reference on the selected factors: their ids, or the filters that
 * selected them all.
 *
 * @param {FactorSelection} selection
 * @param {import("./emissionFactorBreakdownApi").BreakdownFilters} filters
 * @param {number|null} referenceId
 * @param {string[]} targets
 * @returns {{ reference_id: number|null, targets: string[], factor_ids?: number[], filters?: object }}
 */
export const buildReferenceChange = (selection, filters, referenceId, targets) => {
  const change = { reference_id: referenceId, targets: REFERENCE_TARGETS.map(({ target }) => target).filter((t) => targets.includes(t)) };
  if (!selection.allMatching) return { ...change, factor_ids: [...selection.ids] };
  return {
    ...change,
    filters: {
      search: filters.search || undefined,
      scope: filters.scope || undefined,
      ghg_database_id: filters.databaseId ? Number(filters.databaseId) : undefined,
      calculation_method: filters.method || undefined,
    },
  };
};

/**
 * @param {{ title: string, url: string, note: string }} draft
 * @returns {string} what is wrong with the draft, or "" when it can be saved
 */
export const referenceDraftError = (draft) => {
  if (!draft.title.trim()) return "Title is required";
  const url = draft.url.trim();
  if (url && !/^https?:\/\/\S+$/i.test(url)) return "Link must start with http:// or https://";
  return "";
};
